import { z } from 'zod';
import { appointmentStatuses, type AppointmentSource, type AppointmentStatus } from '../enums.js';
import { dateOnlySchema } from './patients.js';

/**
 * Recherche d'agenda : la liste qui permet de retrouver les rendez-vous d'un patient, d'en
 * supprimer plusieurs, et de les imprimer pour les lui remettre.
 *
 * Ce n'est pas la fenêtre de la grille. Celle-ci est bornée à deux mois parce qu'elle lit tout
 * le cabinet ; ici le praticien filtre, et la période peut couvrir une année de suivi.
 */

/** Écart maximal entre les deux dates civiles, en jours. Au-delà, on resserre le filtre. */
export const AGENDA_SEARCH_MAX_DAYS = 366;

/** Plafond de lignes renvoyées. La réponse dit quand il est atteint. */
export const AGENDA_SEARCH_MAX_ROWS = 200;

/** Nombre maximal d'identifiants acceptés par une suppression. */
export const AGENDA_SEARCH_DELETE_MAX = 100;

export const agendaSearchQuerySchema = z
  .object({
    from: dateOnlySchema,
    to: dateOnlySchema,
    /** Nom ou prénom, partie du nom. Absent : pas de filtre patient. */
    patientQuery: z.string().trim().min(1).max(128).optional(),
    practitionerMemberId: z.uuid().optional(),
    status: z.enum(appointmentStatuses).optional(),
  })
  .strict();
export type AgendaSearchQuery = z.infer<typeof agendaSearchQuerySchema>;

export const agendaSearchDeleteSchema = z
  .object({
    ids: z.array(z.uuid()).min(1).max(AGENDA_SEARCH_DELETE_MAX),
  })
  .strict();
export type AgendaSearchDeleteInput = z.infer<typeof agendaSearchDeleteSchema>;

/**
 * Pourquoi une ligne de la recherche ne part pas.
 * `doctena` : le rendez-vous vient de Doctena.
 * `consultation` : une consultation y est liée.
 * `invoiced` : cette consultation porte une facture encore en vigueur.
 * `forbidden` : l'appelant n'est ni le créateur ni le propriétaire.
 */
export const agendaSearchBlockReasons = [
  'doctena',
  'consultation',
  'invoiced',
  'forbidden',
] as const;
export type AgendaSearchBlockReason = (typeof agendaSearchBlockReasons)[number];

/** Nom affiché, dans l'ordre du produit (nom puis prénom) côté écran. */
export interface AgendaSearchPersonDto {
  firstName: string;
  lastName: string;
}

/**
 * Une ligne de résultat. Pas de note, pas de motif, pas de schéma : cette liste sert à
 * retrouver un créneau, pas à lire le dossier.
 */
export interface AgendaSearchHitDto {
  id: string;
  patientId: string | null;
  patient: AgendaSearchPersonDto | null;
  practitionerMemberId: string;
  practitioner: AgendaSearchPersonDto;
  startsAt: string;
  endsAt: string;
  status: AppointmentStatus;
  source: AppointmentSource;
  /** Faux quand `blockReason` est posé. La synchro bidirectionnelle, elle, ferme tout le geste. */
  deletable: boolean;
  blockReason: AgendaSearchBlockReason | null;
}

export interface AgendaSearchDto {
  items: AgendaSearchHitDto[];
  truncated: boolean;
  /**
   * Vrai quand la connexion Doctena du cabinet est active et que son réglage
   * `bidirectionalSync` l'est aussi. La suppression depuis cette recherche est alors fermée,
   * y compris pour les rendez-vous saisis dans OstéoPro : les effacer ici les laisserait chez
   * Doctena.
   */
  bidirectionalSync: boolean;
}

export interface AgendaSearchKeptDto {
  id: string;
  startsAt: string;
  reason: AgendaSearchBlockReason;
}

export interface AgendaSearchDeleteDto {
  deleted: { id: string; startsAt: string }[];
  kept: AgendaSearchKeptDto[];
}
